import * as React from "react";
import { useEffect, useRef, useState, useMemo } from "react";
import { setCaretPosition, getInputSelection } from "./util/caret-util";
import { isTouchEnabled } from "./util/touch-util";
import getCaretCoordinates from "textarea-caret";
import { TriggerKeys } from "./constants/TriggerKeys";
import { getTransliterateSuggestions } from "./util/suggestions-util";
// import TextareaAutosize from "react-textarea-autosize";
// import { useLogger } from "../../../utils/UnifiedLogger";
import PropTypes from "prop-types";






const KEY_UP = "ArrowUp";
const KEY_DOWN = "ArrowDown";
const KEY_LEFT = "ArrowLeft";
const KEY_RIGHT = "ArrowRight";
const KEY_ESCAPE = "Escape";

const OPTION_LIST_Y_OFFSET = 10;
const OPTION_LIST_MIN_WIDTH = 100;

export const KpmgTransliterate = (props) => {
    // const { logInfo, logError } = useLogger("KpmgTransliterate", false);

    const logInfo = (...args) => {
        console.log("[KPMG TRANSLITRATE]: ", args);
    }

    const {
        renderComponent = (props) => <input {...props} />,
        lang = "hi",
        offsetX = 0,
        offsetY = 10,
        onChange,
        onChangeText,
        onBlur,
        value,
        onKeyDown,
        containerClassName = "",
        containerStyles = {},
        activeItemStyles = {},
        maxOptions = 5,
        suggestionPosition = "top",
        hideSuggestionBoxOnMobileDevices = false,
        hideSuggestionBoxBreakpoint = 450,
        triggerKeys = [
            TriggerKeys.KEY_SPACE,
            TriggerKeys.KEY_ENTER,
            TriggerKeys.KEY_RETURN,
            TriggerKeys.KEY_TAB,
        ],
        insertCurrentSelectionOnBlur = true,
        showCurrentWordAsLastSuggestion = true,
        enabled = true,
        horizontalView = false,
        apiKey = "",
        clearOptionsSignal,
        ...rest
    } = props;


    const [left, setLeft] = useState(0);
    const [top, setTop] = useState(0);
    const [selection, setSelection] = useState(0);
    const [matchStart, setMatchStart] = useState(-1);
    const [matchEnd, setMatchEnd] = useState(-1);
    const inputRef = useRef(null);
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
    const [direction, setDirection] = useState("ltr");
    const [googleFont, setGoogleFont] = useState(null);
    const [options, setOptions] = useState([]);
    const [logJsonArray, setLogJsonArray] = useState([]);
    const [numSpaces, setNumSpaces] = useState(0);
    const [parentUuid, setParentUuid] = useState("0");
    const [uuid, setUuid] = useState(
        Math.random().toString(36).slice(2, 11)
    );

    const [subStrLength, setSubStrLength] = useState(0);
    const [restart, setRestart] = useState(true);
    const isEnglishLanguage = typeof lang === "string" && ["en", "english"].includes(lang.toLowerCase().split(/[-_]/)[0]);

    const shouldRenderSuggestions = useMemo(
        () =>
            hideSuggestionBoxOnMobileDevices
                ? windowSize.width > hideSuggestionBoxBreakpoint
                : true,
        [windowSize, hideSuggestionBoxBreakpoint, hideSuggestionBoxOnMobileDevices],
    );

    const reset = () => {
        setSelection(0);
        setOptions([]);
    }
    useEffect(() => {
        if (clearOptionsSignal !== undefined) {
            setOptions([]);
        }
    }, [clearOptionsSignal]);


    const handleSelection = (index) => {
        const currentString = value;
        const newValue =
            currentString.substring(0, matchStart) +
            options[index] +
            " " +
            currentString.substring(matchEnd + 1, currentString.length);

        if (logJsonArray.length) {
            let lastLogJson = logJsonArray[logJsonArray.length - 1];
            let logJson = {
                keystrokes: lastLogJson.keystrokes,
                results: lastLogJson.results,
                opted: options[index],
                created_at: new Date().toISOString(),
                language: lang
            };
            setLogJsonArray([...logJsonArray, logJson]);
            setNumSpaces(numSpaces + 1);
        }

        setTimeout(() => {
            setCaretPosition(inputRef.current, matchStart + options[index].length + 1);
        }, 1);

        const e = {
            target: { value: newValue },
        };
        onChangeText(newValue);
        onChange && onChange(e);
        reset();
        return inputRef.current?.focus();
    };

    const renderSuggestions = async (lastWord, wholeText) => {
        console.log("[TRANSLITERATE_DEBUG] renderSuggestions called", { lastWord, shouldRenderSuggestions, lang, enabled });

        if (!shouldRenderSuggestions || isEnglishLanguage) {
            console.warn("[TRANSLITERATE_DEBUG] BLOCKED: shouldRenderSuggestions is false", { windowSize, hideSuggestionBoxOnMobileDevices, hideSuggestionBoxBreakpoint });
            return;
        }

        const targetLang = lang;
        console.log("[TRANSLITERATE_DEBUG] Calling getTransliterateSuggestions", { lastWord, targetLang });
        const data = await getTransliterateSuggestions(lastWord, {
            showCurrentWordAsLastSuggestion,
            lang: targetLang,
        });
        console.log("[TRANSLITERATE_DEBUG] Got suggestions", { data });
        if (isEnglishLanguage) {
            return;
        }
        setOptions(data ?? []);

        let logJson = {
            keystrokes: wholeText,
            results: data,
            opted: "",
            created_at: new Date().toISOString(),
            language: targetLang
        };

        if (restart) {
            setRestart(false);
            setLogJsonArray([logJson]);
        } else {
            setLogJsonArray([...logJsonArray, logJson]);
        }

    };

    const getDirectionAndFont = async (lang) => {
        const langList = ['en', 'hi'];
        const langObj = langList?.find((l) => l.LangCode === lang);
        return [
            langObj?.Direction ?? "ltr",
            langObj?.GoogleFont,
            langObj?.FallbackFont,
        ];
    };

    const handleChange = (e) => {
        const value = e.currentTarget.value;
        logInfo("INPUT_CHANGED", "Input changed", { value });


        if (numSpaces === 0 || restart) {
            if (value.length >= 4) {
                setSubStrLength(value.length - 4);
            } else {
                setSubStrLength(0);
            }
        }

        onChange && onChange(e);
        onChangeText(value);

        if (isEnglishLanguage) {
            reset();
            return;
        }

        if (!shouldRenderSuggestions) {
            console.warn("[TRANSLITERATE_DEBUG] BLOCKED in handleChange: shouldRenderSuggestions is false", { windowSize, hideSuggestionBoxOnMobileDevices, hideSuggestionBoxBreakpoint });
            return;
        }

        const caret = getInputSelection(e.target).end;
        const input = inputRef.current;

        if (!input) {
            console.warn("[TRANSLITERATE_DEBUG] BLOCKED: inputRef.current is null");
            return;
        }

        const caretPos = getCaretCoordinates(input, caret);

        const indexOfLastSpace =
            value.lastIndexOf(" ", caret - 1) < value.lastIndexOf("\n", caret - 1)
                ? value.lastIndexOf("\n", caret - 1)
                : value.lastIndexOf(" ", caret - 1);

        setMatchStart(indexOfLastSpace + 1);
        setMatchEnd(caret - 1);

        const currentWord = value.slice(indexOfLastSpace + 1, caret);
        console.log("[TRANSLITERATE_DEBUG] Gate check", { currentWord, enabled, shouldRenderSuggestions, caret, indexOfLastSpace });
        if (currentWord && enabled) {
            if (numSpaces === 0 || restart) {
                if (value.length >= 4) {
                    renderSuggestions(currentWord, value.substr(value.length - 4, value.length));
                } else {
                    renderSuggestions(currentWord, value.substr(0, value.length));
                }
            } else {
                renderSuggestions(currentWord, value.substr(subStrLength, value.length));
            }

            const rect = input.getBoundingClientRect();

            const left = Math.min(
                caretPos.left,
                rect.width - OPTION_LIST_MIN_WIDTH / 2,
            );
            const top = suggestionPosition === "top"
                ? Math.max(caretPos.top - OPTION_LIST_Y_OFFSET - 30, 0)
                : Math.min(caretPos.top + OPTION_LIST_Y_OFFSET, rect.height);

            setTop(top);
            setLeft(left);
        } else {
            reset();
        }
    };

    const handleKeyDown = (event) => {
        const helperVisible = options.length > 0;

        if (event.key === "Enter" && event.shiftKey) {
            reset();
            onKeyDown && onKeyDown(event);
            return;
        }

        if (helperVisible) {
            if (triggerKeys.includes(event.key)) {
                event.preventDefault();
                handleSelection(selection);
                // If Enter was pressed, also notify the parent so the message can be sent
                if (event.key === "Enter") {
                    onKeyDown && onKeyDown(event);
                }
            } else {
                switch (event.key) {
                    case KEY_ESCAPE:
                        event.preventDefault();
                        reset();
                        break;
                    case KEY_UP:
                        event.preventDefault();
                        setSelection((options.length + selection - 1) % options.length);
                        break;
                    case KEY_DOWN:
                        event.preventDefault();
                        setSelection((selection + 1) % options.length);
                        break;
                    case KEY_LEFT:
                    case KEY_RIGHT:
                        onKeyDown && onKeyDown(event);
                        break;
                    default:
                        onKeyDown && onKeyDown(event);
                        break;
                }
            }
        } else {
            onKeyDown && onKeyDown(event);
        }
    };

    const handleBlur = (event) => {
        if (!isTouchEnabled()) {
            if (insertCurrentSelectionOnBlur && options[selection]) {
                handleSelection(selection);
            } else {
                reset();
            }
        }
        onBlur && onBlur(event);
    };

    const handleResize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWindowSize({ width, height });
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWindowSize({ width, height });

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (isEnglishLanguage) {
            reset();
        }
    }, [isEnglishLanguage]);

    useEffect(() => {
        getDirectionAndFont(lang).then(([direction, googleFont, fallbackFont]) => {
            setDirection(direction);
            if (googleFont) {
                if (!document.getElementById(`font-${googleFont}`)) {
                    const link = document.createElement("link");
                    link.id = `font-${googleFont}`;
                    link.href = `https://fonts.googleapis.com/css?family=${googleFont}`;
                    link.rel = "stylesheet";
                    document.head.appendChild(link);
                }
                setGoogleFont(`${googleFont}, ${fallbackFont ?? "sans-serif"}`);
            } else {
                setGoogleFont(null);
            }
        });
    }, [lang]);

    return (
        <div
            style={{
                ...containerStyles,
                position: "relative",
            }}
            className={containerClassName}
        >
            {renderComponent({
                onChange: handleChange,
                onKeyDown: handleKeyDown,
                onBlur: handleBlur,
                ref: inputRef,
                value: value,
                "data-testid": "rt-input-component",
                lang: lang,
                style: {
                    direction: direction,
                    ...(googleFont && { fontFamily: googleFont }),
                },
                ...rest,
            })}
            {shouldRenderSuggestions && options.length > 0 && (
                <div
                    role="listbox"
                    aria-label="Transliteration suggestions"
                    style={{
                        position: "absolute",
                        top: suggestionPosition === "top" ? "auto" : "calc(100% + 4px)",
                        bottom: suggestionPosition === "top" ? "calc(100% + 4px)" : "auto",
                        left: 0,
                        minWidth: OPTION_LIST_MIN_WIDTH,
                        maxWidth: "min(280px, 100%)",
                        overflow: "hidden",
                        background: "#ffffff",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.16)",
                        zIndex: 1000,
                    }}
                >
                    {options.slice(0, maxOptions).map((option, index) => {
                        const isActive = index === selection;

                        return (
                            <button
                                key={`${option}-${index}`}
                                type="button"
                                role="option"
                                aria-selected={isActive}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    handleSelection(index);
                                }}
                                style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "8px 14px",
                                    border: 0,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    color: isActive ? "#ffffff" : "#111827",
                                    background: isActive ? "rgb(0, 112, 109)" : "#ffffff",
                                    fontSize: "14px",
                                    lineHeight: 1.35,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    ...activeItemStyles,
                                }}
                            >
                                {option}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


KpmgTransliterate.propTypes = {
    renderComponent: PropTypes.func,
    lang: PropTypes.string,
    offsetX: PropTypes.number,
    offsetY: PropTypes.number,
    onChange: PropTypes.func,
    onChangeText: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    value: PropTypes.string.isRequired,
    onKeyDown: PropTypes.func,
    containerClassName: PropTypes.string,
    containerStyles: PropTypes.object,
    activeItemStyles: PropTypes.object,
    maxOptions: PropTypes.number,
    suggestionPosition: PropTypes.string,
    hideSuggestionBoxOnMobileDevices: PropTypes.bool,
    hideSuggestionBoxBreakpoint: PropTypes.number,
    triggerKeys: PropTypes.arrayOf(PropTypes.string),
    insertCurrentSelectionOnBlur: PropTypes.bool,
    showCurrentWordAsLastSuggestion: PropTypes.bool,
    enabled: PropTypes.bool,
    horizontalView: PropTypes.bool,
    apiKey: PropTypes.string,
};